import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { format } from 'date-fns';
import {
  getProject, updateProject, getExpenses, createExpense, saveProjectAsTemplate,
  getProjectVideos, getShopConfig, splitMaterial, getTools,
} from '../utils/api';
import { useApp } from '../context/AppContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import DocumentsSection from '../components/documents/DocumentsSection';

const fmt = (d) => d ? format(new Date(d), 'MMM d, yyyy') : '—';
const fmtMoney = (n) => n != null ? `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null;
const fmtMoneyOrDash = (n) => fmtMoney(n) ?? '—';

const STATUSES = ['idea', 'planned', 'in-progress', 'completed', 'on-hold'];
const DIFF_LABELS = { easy: '🟢 Easy DIY', moderate: '🟡 Moderate DIY', hard: '🔴 Challenging DIY', 'hire-out': '⚠️ Hire a Pro' };

// Builds affiliate-tagged search URLs for each configured retailer.
// NOTE: Home Depot and Lowe's URL templates should be updated with the exact
// tracking format provided by Impact / CJ Affiliate after account setup.
const buildShopUrls = (name, config) => {
  if (!config) return {};
  const q = encodeURIComponent(name);
  const urls = {};
  if (config.amazon) urls.amazon = `https://www.amazon.com/s?k=${q}&tag=${config.amazon}`;
  if (config.homeDepot) urls.homeDepot = `https://www.homedepot.com/s/${q}`;
  if (config.lowes) urls.lowes = `https://www.lowes.com/search?searchTerm=${q}`;
  return urls;
};

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { removeProject } = useApp();
  const { confirm } = useConfirm();
  const { showToast } = useToast();
  const [project, setProject] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [newExpense, setNewExpense] = useState({ amount: '', description: '', vendor: '', date: '', category: 'supplies' });

  // YouTube tutorials
  const [videos, setVideos] = useState([]);
  const [videosLoading, setVideosLoading] = useState(true);
  const [videosEnabled, setVideosEnabled] = useState(false);

  // Affiliate shop links config
  const [shopConfig, setShopConfig] = useState(null);

  // Tool inventory — for "In inventory" badges on tool rows
  const [inventoryTools, setInventoryTools] = useState([]);

  // Shopping list split
  const [splittingIdx, setSplittingIdx] = useState(null);
  const [splitPreview, setSplitPreview] = useState([]);
  const [splitSaving, setSplitSaving] = useState(false);

  const load = () =>
    getProject(id)
      .then(({ data }) => setProject(data))
      .catch(() => setLoadError('Project not found.'));

  const loadExpenses = () =>
    getExpenses({ projectId: id })
      .then(({ data }) => setExpenses(data))
      .catch(() => {});

  useEffect(() => {
    load();
    loadExpenses();
    setVideosLoading(true);
    getProjectVideos(id)
      .then(({ data }) => { setVideosEnabled(data.enabled); setVideos(data.videos || []); })
      .catch(() => {})
      .finally(() => setVideosLoading(false));
    getShopConfig()
      .then(({ data }) => setShopConfig(data))
      .catch(() => {});
    getTools()
      .then(({ data }) => setInventoryTools(data))
      .catch(() => {});
  }, [id]); // eslint-disable-line

  const isInInventory = (toolName) =>
    inventoryTools.some((t) => t.name.toLowerCase() === toolName.toLowerCase());

  const hasShopLinks = shopConfig && (shopConfig.amazon || shopConfig.homeDepot || shopConfig.lowes);

  const handleSaveAsTemplate = async () => {
    setSavingTemplate(true);
    try {
      await saveProjectAsTemplate(id, {});
      showToast('Saved as template — find it in Projects → Templates', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save template', 'error');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDelete = async () => {
    if (!(await confirm({ title: 'Delete project', message: `"${project.title}" and its plan will be permanently removed.` }))) return;
    try {
      await removeProject(id);
      navigate('/projects');
    } catch { /* toast shown by removeProject */ }
  };

  const handleStatusChange = async (e) => {
    const status = e.target.value;
    setUpdatingStatus(true);
    try {
      const { data } = await updateProject(id, { status });
      setProject(data);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const toggleStep = async (index) => {
    const current = project.completedSteps || [];
    const updated = current.includes(index)
      ? current.filter((i) => i !== index)
      : [...current, index];
    const { data } = await updateProject(id, { completedSteps: updated });
    setProject(data);
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    const { data } = await createExpense({
      ...newExpense,
      amount: parseFloat(newExpense.amount),
      date: newExpense.date || new Date().toISOString(),
      linkedProject: id,
    });
    setExpenses((prev) => [data, ...prev]);
    setNewExpense({ amount: '', description: '', vendor: '', date: '', category: 'supplies' });
    setShowAddExpense(false);
  };

  const handleSplit = async (i, name) => {
    setSplittingIdx(i);
    setSplitPreview([]);
    try {
      const { data } = await splitMaterial(name, project?.title);
      if (data.items.length <= 1) {
        showToast('This looks like a single item — no split needed', 'info');
        setSplittingIdx(null);
        return;
      }
      setSplitPreview(data.items);
    } catch (err) {
      showToast(err.response?.data?.message || 'Split failed', 'error');
      setSplittingIdx(null);
    }
  };

  const handleSplitConfirm = async () => {
    const newMaterials = [
      ...project.materials.slice(0, splittingIdx),
      ...splitPreview,
      ...project.materials.slice(splittingIdx + 1),
    ];
    setSplitSaving(true);
    try {
      const { data } = await updateProject(id, { materials: newMaterials });
      setProject(data);
      setSplittingIdx(null);
      setSplitPreview([]);
      showToast(`Split into ${splitPreview.length} items`, 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save split', 'error');
    } finally {
      setSplitSaving(false);
    }
  };

  const cancelSplit = () => { setSplittingIdx(null); setSplitPreview([]); };

  if (loadError) return <div className="page"><p className="empty-state">{loadError}</p></div>;
  if (!project) return <div className="loading">Loading…</div>;

  const materialTotal = project.materials?.reduce((s, m) => s + (m.estimatedCost || 0), 0) ?? 0;
  const toolsNeeded = project.tools?.filter((t) => !t.owned) ?? [];
  const toolsTotal = toolsNeeded.reduce((s, t) => s + (t.estimatedCost || 0), 0);
  const grandTotal = materialTotal + toolsTotal;
  const completedSteps = project.completedSteps || [];
  const stepCount = project.steps?.length || 0;
  const progressPct = stepCount > 0 ? Math.round((completedSteps.length / stepCount) * 100) : 0;
  const expenseTotal = expenses.reduce((s, e) => s + (e.amount || 0), 0);
  const diyActual = project.actualCost ?? grandTotal;
  const diySavings = project.professionalCost != null ? project.professionalCost - diyActual : null;
  const roi = project.estimatedValueAdd != null && diyActual > 0
    ? Math.round(((project.estimatedValueAdd - diyActual) / diyActual) * 100)
    : null;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>{project.title}</h1>
          <p className="page-subtitle">
            <span className={`badge badge-priority-${project.priority}`}>{project.priority}</span>
            {' '}<span className="badge">{project.category}</span>
            {project.diyDifficulty && DIFF_LABELS[project.diyDifficulty] && (
              <>{' '}<span className="badge" style={{ background: '#fef9c3', color: '#854d0e', border: '1px solid #fde68a' }}>{DIFF_LABELS[project.diyDifficulty]}</span></>
            )}
            {project.permitRequired && (
              <>{' '}<span className="badge" style={{ background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' }}>🏛 Permit Required</span></>
            )}
          </p>
        </div>
        <div className="header-actions">
          <select value={project.status} onChange={handleStatusChange} disabled={updatingStatus} className="status-select">
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace('-', ' ')}</option>)}
          </select>
          <button className="btn btn-secondary" onClick={handleSaveAsTemplate} disabled={savingTemplate}>
            {savingTemplate ? '…' : '⎘ Save as Template'}
          </button>
          <Link to={`/projects/${id}/chat`} className="btn btn-secondary">💬 Get Help</Link>
          <Link to={`/projects/${id}/rescope`} className="btn btn-secondary">Rescope with AI</Link>
          <button className="btn btn-danger" onClick={handleDelete}>Delete</button>
        </div>
      </div>

      <div className="detail-grid">
        {/* Left column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {/* Overview */}
          <section className="detail-section">
            <h2>Overview</h2>
            {project.description && <p style={{ fontSize: '0.9rem', marginBottom: '1rem', lineHeight: 1.6 }}>{project.description}</p>}
            <dl className="detail-list">
              <dt>Status</dt>
              <dd><span className={`badge badge-status-${project.status}`}>{project.status.replace('-', ' ')}</span></dd>
              <dt>Target Date</dt><dd>{fmt(project.targetDate)}</dd>
              <dt>Duration</dt><dd>{project.estimatedDuration || '—'}</dd>
              {project.bestSeason && <><dt>Best Season</dt><dd style={{ textTransform: 'capitalize' }}>{project.bestSeason}</dd></>}
              <dt>Est. Cost</dt><dd>{fmtMoneyOrDash(project.estimatedCost)}</dd>
              {project.actualCost != null && <><dt>Actual Cost</dt><dd>{fmtMoneyOrDash(project.actualCost)}</dd></>}
              {project.completedDate && <><dt>Completed</dt><dd>{fmt(project.completedDate)}</dd></>}
            </dl>
            {project.diagram && (
              <div className="detail-notes">
                <h3>📐 Layout</h3>
                <pre className="intake-diagram">{project.diagram}</pre>
              </div>
            )}
            {project.notes && (
              <div className="detail-notes">
                <h3>Notes</h3>
                <p>{project.notes}</p>
              </div>
            )}
          </section>

          {/* ROI panel */}
          {(project.professionalCost != null || project.estimatedValueAdd != null) && (
            <section className="detail-section">
              <h2>💰 ROI Summary</h2>
              <dl className="detail-list">
                <dt>DIY Cost</dt><dd>{fmtMoneyOrDash((project.actualCost ?? grandTotal) || project.estimatedCost)}</dd>
                {project.professionalCost != null && <><dt>Pro Cost</dt><dd>{fmtMoneyOrDash(project.professionalCost)}</dd></>}
                {diySavings != null && (
                  <><dt>DIY Savings</dt><dd style={{ color: diySavings > 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 700 }}>{diySavings > 0 ? '+' : ''}{fmtMoneyOrDash(diySavings)}</dd></>
                )}
                {project.estimatedValueAdd != null && <><dt>Est. Value Added</dt><dd style={{ fontWeight: 600 }}>{fmtMoneyOrDash(project.estimatedValueAdd)}</dd></>}
                {roi != null && <><dt>ROI</dt><dd style={{ fontWeight: 700, color: roi >= 0 ? 'var(--success)' : 'var(--danger)' }}>{roi > 0 ? '+' : ''}{roi}%</dd></>}
              </dl>
            </section>
          )}

          {/* Steps */}
          {stepCount > 0 && (
            <section className="detail-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h2 style={{ margin: 0 }}>Steps</h2>
                <span style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>{completedSteps.length} of {stepCount} complete</span>
              </div>
              {stepCount > 0 && (
                <div className="step-progress-bar">
                  <div className="step-progress-fill" style={{ width: `${progressPct}%` }} />
                </div>
              )}
              <ol className="project-steps-list" style={{ marginTop: '0.75rem' }}>
                {project.steps.map((step, i) => {
                  const done = completedSteps.includes(i);
                  return (
                    <li
                      key={i}
                      className={`project-step project-step-check${done ? ' done' : ''}`}
                      onClick={() => toggleStep(i)}
                    >
                      <span className="step-checkbox">{done ? '☑' : '☐'}</span>
                      <span>{step}</span>
                    </li>
                  );
                })}
              </ol>
            </section>
          )}

          {/* Expenses */}
          <section className="detail-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h2 style={{ margin: 0 }}>Expenses</h2>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowAddExpense((v) => !v)}>
                {showAddExpense ? 'Cancel' : '+ Add'}
              </button>
            </div>
            {showAddExpense && (
              <form onSubmit={handleAddExpense} className="inline-expense-form">
                <input type="number" placeholder="Amount" value={newExpense.amount} onChange={(e) => setNewExpense((f) => ({ ...f, amount: e.target.value }))} required step="0.01" />
                <input type="text" placeholder="Description" value={newExpense.description} onChange={(e) => setNewExpense((f) => ({ ...f, description: e.target.value }))} />
                <input type="text" placeholder="Vendor" value={newExpense.vendor} onChange={(e) => setNewExpense((f) => ({ ...f, vendor: e.target.value }))} />
                <input type="date" value={newExpense.date} onChange={(e) => setNewExpense((f) => ({ ...f, date: e.target.value }))} />
                <button type="submit" className="btn btn-primary btn-sm">Save</button>
              </form>
            )}
            {expenses.length > 0 ? (
              <>
                <div className="expense-list">
                  {expenses.map((exp) => (
                    <div key={exp._id} className="expense-row">
                      <span className="expense-desc">{exp.description || exp.category}</span>
                      {exp.vendor && <span className="expense-vendor">{exp.vendor}</span>}
                      <span className="expense-amount">{fmtMoneyOrDash(exp.amount)}</span>
                    </div>
                  ))}
                </div>
                <div className="project-cost-row" style={{ marginTop: '0.5rem' }}>
                  <span>Total logged</span>
                  <span>{fmtMoneyOrDash(expenseTotal)}</span>
                </div>
                {project.estimatedCost != null && (
                  <div style={{ fontSize: '0.8rem', color: expenseTotal > project.estimatedCost ? 'var(--danger)' : 'var(--muted)', marginTop: '0.25rem', textAlign: 'right' }}>
                    {expenseTotal > project.estimatedCost
                      ? `⚠ ${fmtMoneyOrDash(expenseTotal - project.estimatedCost)} over estimate`
                      : `${fmtMoneyOrDash(project.estimatedCost - expenseTotal)} remaining`}
                  </div>
                )}
              </>
            ) : (
              <p className="empty-state" style={{ fontSize: '0.85rem', padding: '0.5rem 0' }}>No expenses logged yet.</p>
            )}
          </section>
        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {project.materials?.length > 0 && (
            <section className="detail-section">
              <h2>🛒 Shopping List</h2>
              <div className="project-materials">
                {project.materials.map((m, i) => {
                  const shopUrls = buildShopUrls(m.name, shopConfig);
                  const isSplittingThis = splittingIdx === i;
                  const isWaiting = splittingIdx === i && splitPreview.length === 0;

                  return (
                    <div key={i}>
                      <div className="project-material-row">
                        <div className="project-material-info">
                          <span className="project-material-name">{m.name}</span>
                          {m.quantity && <span className="project-material-qty">{m.quantity}</span>}
                          {m.notes && <span className="project-material-note">{m.notes}</span>}
                          {Object.keys(shopUrls).length > 0 && (
                            <div className="material-shop-links">
                              {shopUrls.amazon && <a href={shopUrls.amazon} target="_blank" rel="noopener noreferrer sponsored" className="shop-link shop-link-amazon">Amazon</a>}
                              {shopUrls.homeDepot && <a href={shopUrls.homeDepot} target="_blank" rel="noopener noreferrer sponsored" className="shop-link shop-link-hd">HD</a>}
                              {shopUrls.lowes && <a href={shopUrls.lowes} target="_blank" rel="noopener noreferrer sponsored" className="shop-link shop-link-lowes">Lowe's</a>}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', flexShrink: 0 }}>
                          {m.estimatedCost != null && <span className="project-material-cost">{fmtMoneyOrDash(m.estimatedCost)}</span>}
                          <button
                            className="btn btn-secondary btn-sm split-btn"
                            onClick={() => handleSplit(i, m.name)}
                            disabled={splittingIdx !== null}
                            title="Split into individual items"
                          >
                            {isWaiting ? '…' : '✂'}
                          </button>
                        </div>
                      </div>
                      {isSplittingThis && splitPreview.length > 0 && (
                        <div className="split-preview">
                          <p className="split-preview-label">Split into {splitPreview.length} items:</p>
                          <ul className="split-preview-list">
                            {splitPreview.map((item, j) => (
                              <li key={j}>
                                <span className="split-preview-name">{item.name}</span>
                                {item.quantity && <span className="split-preview-meta">{item.quantity}</span>}
                                {item.estimatedCost != null && <span className="split-preview-meta">~${item.estimatedCost}</span>}
                              </li>
                            ))}
                          </ul>
                          <div className="split-preview-actions">
                            <button className="btn btn-primary btn-sm" onClick={handleSplitConfirm} disabled={splitSaving}>
                              {splitSaving ? 'Saving…' : 'Confirm Split'}
                            </button>
                            <button className="btn btn-secondary btn-sm" onClick={cancelSplit} disabled={splitSaving}>Cancel</button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                {materialTotal > 0 && (
                  <div className="project-cost-row"><span>Materials subtotal</span><span>{fmtMoneyOrDash(materialTotal)}</span></div>
                )}
              </div>
              {hasShopLinks && <p className="shop-disclosure">Links may earn a commission.</p>}
            </section>
          )}

          {project.tools?.length > 0 && (
            <section className="detail-section">
              <h2>🔧 Tools</h2>
              <ul className="project-tools-list">
                {project.tools.map((t, i) => {
                  const inInventory = isInInventory(t.name);
                  return (
                    <li key={i} className={`project-tool${t.owned || inInventory ? ' owned' : ''}`}>
                      <span className="project-tool-icon">{t.owned || inInventory ? '✓' : '+'}</span>
                      <span className="project-tool-name">{t.name}</span>
                      {!t.owned && !inInventory && t.estimatedCost != null && <span className="project-tool-cost">{fmtMoneyOrDash(t.estimatedCost)}</span>}
                      {inInventory && <span className="project-tool-own tool-in-inventory">in inventory</span>}
                      {t.owned && !inInventory && <span className="project-tool-own">have it</span>}
                    </li>
                  );
                })}
              </ul>
              {toolsTotal > 0 && (
                <div className="project-cost-row" style={{ marginTop: '0.5rem' }}><span>Tools to buy</span><span>{fmtMoneyOrDash(toolsTotal)}</span></div>
              )}
            </section>
          )}

          {grandTotal > 0 && (
            <div className="project-grand-total">
              <span>Estimated Total</span>
              <span>{fmtMoneyOrDash(grandTotal)}</span>
            </div>
          )}

          {/* YouTube Tutorial Videos */}
          {videosEnabled && (
            <section className="detail-section">
              <h2>📺 Tutorial Videos</h2>
              {videosLoading ? (
                <p style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>Loading tutorials…</p>
              ) : videos.length > 0 ? (
                <div className="video-list">
                  {videos.map((v) => (
                    <a
                      key={v.videoId}
                      href={`https://www.youtube.com/watch?v=${v.videoId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="video-card"
                    >
                      {v.thumbnail && <img src={v.thumbnail} alt="" className="video-thumb" />}
                      <div className="video-info">
                        <span className="video-title">{v.title}</span>
                        <span className="video-channel">{v.channelTitle}</span>
                      </div>
                    </a>
                  ))}
                </div>
              ) : (
                <p style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>No tutorials found for this project.</p>
              )}
            </section>
          )}
        </div>
      </div>

      <div style={{ marginTop: '1.5rem' }}>
        <DocumentsSection projectId={id} />
      </div>
    </div>
  );
};

export default ProjectDetail;
